import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Landmark, Wallet, CheckCircle2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { eur, fmtDate, Loading, Field } from "@/components/common";
import BackNav from "@/components/BackNav";
import PayPalCheckout from "@/components/PayPalCheckout";
import WhatsAppLink from "@/components/WhatsAppLink";
import { Banknote, Check } from "lucide-react";
import { REC_LABELS } from "@/constants/recorrenze";

const METHODS = [
  { id: "carta", label: "Carta", icon: CreditCard, hint: "Visa e Mastercard, anche senza conto PayPal" },
  { id: "paypal", label: "PayPal", icon: Wallet, hint: "Paga con il tuo conto PayPal" },
  { id: "bonifico", label: "Bonifico bancario", icon: Landmark, hint: "Dati bancari mostrati dopo la conferma dell'ordine" },
  { id: "contanti", label: "Contanti", icon: Banknote, hint: "Concordiamo insieme pagamento e appuntamento" },
];

export default function Checkout() {
  const [params] = useSearchParams();
  const type = params.get("type") || "singola";
  const planId = params.get("plan") || "";
  const STORAGE_KEY = `sa_checkout_${params.get("type") || "singola"}`;
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [flowers, setFlowers] = useState([]);
  const [graves, setGraves] = useState(null);
  const [trial, setTrial] = useState(null);
  const [credits, setCredits] = useState(null);
  const [plan, setPlan] = useState(null);

  const [graveId, setGraveId] = useState("");
  const [flowerId, setFlowerId] = useState("");
  const puliziaParam = params.get("pulizia");
  const [cleaning, setCleaning] = useState(
    ["profonda", "specialistica"].includes(puliziaParam) ? "specialistica" : puliziaParam === "approfondita" ? "approfondita" : "inclusa"
  );
  const [notes, setNotes] = useState("");
  const [method, setMethod] = useState("carta");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [pending, setPending] = useState(null);

  // Conferma ordine (bonifico/contanti) e pagamento online: viste nuove nella stessa
  // rotta → riparti sempre dall'alto.
  useEffect(() => {
    if (done || pending) window.scrollTo(0, 0);
  }, [done, pending]);
  const [extrasCatalog, setExtrasCatalog] = useState([]);
  const [extraIds, setExtraIds] = useState([]);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [restored, setRestored] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const [birthday, setBirthday] = useState("");
  const [delegation, setDelegation] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/config"),
      api.get("/flowers"),
      api.get("/customer/graves"),
      api.get("/customer/trial-status"),
      api.get("/customer/credits"),
    ]).then(([c, f, g, t, cr]) => {
      setConfig(c.data);
      setFlowers(f.data);
      setGraves(g.data);
      let saved = null;
      try { saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null"); } catch { saved = null; }
      if (g.data.length > 0) setGraveId(saved?.graveId && g.data.some((x) => x.id === saved.graveId) ? saved.graveId : g.data[0].id);
      if (saved) {
        setFlowerId(saved.flowerId || "");
        if (saved.cleaning) setCleaning(saved.cleaning);
        setNotes(saved.notes || "");
        if (saved.method) setMethod(saved.method);
        if (Array.isArray(saved.extraIds)) setExtraIds(saved.extraIds);
      }
      setTrial(t.data);
      setCredits(cr.data);
      setRestored(true);
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => {
    if (type !== "abbonamento" || !planId) return;
    api.get("/offer").then((r) => {
      if (r.data && r.data.id === planId) setPlan(r.data);
      else api.get("/plans").then((p) => setPlan(p.data.find((x) => x.id === planId) || null));
    });
  }, [type, planId]);

  useEffect(() => {
    if (type === "extra") {
      api.get("/customer/extras/delivery-info").then((r) => setDeliveryInfo(r.data)).catch(() => {});
    }
    if (type === "singola" || type === "extra") {
      api.get("/extras?category=extra").then((r) => {
        const list = r.data.filter((e) => !e.quote_only);
        setExtrasCatalog(list);
        if (type === "extra") {
          const pre = (params.get("extras") || "").split(",").filter((id) => list.some((e) => e.id === id));
          setExtraIds((ids) => (ids.length ? ids : pre));
        }
      }).catch(() => {});
      return;
    }
    if (type === "abbonamento" && plan) {
      // Solo le ricorrenze che rientrano nel periodo dell'abbonamento scelto.
      api.get(`/extras/eligible?months=${plan.duration_months}`).then((r) => {
        const eligible = r.data.filter((e) => !e.quote_only);
        setExtrasCatalog(eligible);
        setExtraIds((ids) => ids.filter((id) => eligible.some((e) => e.id === id)));
      }).catch(() => {});
      return;
    }
    setExtrasCatalog([]);
  }, [type, plan]);

  // Le selezioni del checkout restano memorizzate: tornando indietro non si perde nulla.
  useEffect(() => {
    if (!restored) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ graveId, flowerId, cleaning, notes, method, extraIds }));
    } catch {}
  }, [restored, STORAGE_KEY, graveId, flowerId, cleaning, notes, method, extraIds]);

  const flower = flowers.find((f) => f.id === flowerId);
  const flowerSupplement = flower ? Number(flower.supplement ?? Math.max(0, flower.price - (config?.flower_standard_price ?? 6))) : 0;

  const selExtras = extrasCatalog.filter((e) => extraIds.includes(e.id));
  const extrasTotal = selExtras.reduce((s, e) => s + e.price, 0);
  // Supplemento di consegna SOLO per ordini di soli Extra senza intervento già programmato
  // (uscita dedicata). Extra insieme a un'uscita o ricorrenze: consegna sempre inclusa.
  const exitFee = type === "extra" && selExtras.length > 0 && deliveryInfo && !deliveryInfo.free_delivery ? config?.extra_exit_fee ?? 10 : 0;

  const summary = useMemo(() => {
    if (!config) return null;
    if (type === "singola") {
      const isTrial = !trial?.trial_used;
      const base = config.single_visit_price;
      const clean = cleaning === "approfondita" ? config.cleaning_ordinary_price : 0;
      if (cleaning === "specialistica") return { quote: true, total: 0, rows: [["Pulizia specialistica", "Preventivo personalizzato"]] };
      const rows = [
        [isTrial ? "Prima uscita di prova" : "Singola uscita", eur(base)],
        ...(flower ? [[`Fiori: ${flower.name}`, flowerSupplement > 0 ? `+${eur(flowerSupplement)}` : "Inclusi"]] : [["Fiori standard (margherite)", "Inclusi"]]),
        ...(cleaning === "approfondita" ? [["Pulizia approfondita", `+${eur(clean)}`]] : []),
        ...selExtras.map((e) => [`Extra: ${e.name}`, `+${eur(e.price)}`]),
        ...(exitFee > 0 ? [["Uscita aggiuntiva per Extra (una sola volta)", `+${eur(exitFee)}`]] : []),
      ];
      return { quote: false, total: base + flowerSupplement + clean + extrasTotal + exitFee, rows, isTrial };
    }
    if (type === "extra") {
      const rows = [
        ...selExtras.map((e) => [`Extra: ${e.name}`, `+${eur(e.price)}`]),
        exitFee > 0
          ? ["Uscita dedicata per la consegna (una sola volta)", `+${eur(exitFee)}`]
          : ["Consegna durante un intervento già programmato", "Gratuita"],
      ];
      return { quote: false, total: extrasTotal + exitFee, rows };
    }
    if (!plan || !credits) return null;
    const bonus = credits.bonus;
    const credit = bonus?.status === "disponibile" && plan.interventions_total >= 5 ? Math.min(bonus.amount, plan.price) : 0;
    const n = plan.interventions_total;
    const fs = flower ? Number(flower.supplement ?? 0) : 0;
    const cs = cleaning === "approfondita" ? config.cleaning_ordinary_price : 0;
    const rows = [
      [`Abbonamento: ${plan.title || `${plan.frequency} interventi/mese × ${plan.duration_months} mesi`}`, eur(plan.price)],
      [`${n} interventi totali · ${eur(plan.price_per_intervention)} a intervento`, ""],
      ...(flower ? [[`Composizione floreale: ${flower.name}`, fs > 0 ? `+${eur(fs)} × ${n} = +${eur(fs * n)}` : "Inclusa"]] : []),
      ...(cs > 0 ? [["Pulizia approfondita", `+${eur(cs)} × ${n} = +${eur(cs * n)}`]] : []),
      ...(credit > 0 ? [["Bonus prima uscita", `−${eur(credit)}`]] : []),
      ...selExtras.map((e) => [`${REC_LABELS[e.recurrence] || "Ricorrenza"}: ${e.name}`, `+${eur(e.price)}`]),
    ];
    return { quote: false, total: plan.price + (fs + cs) * n - credit + extrasTotal, rows, credit };
  }, [config, type, trial, cleaning, flower, flowerSupplement, plan, credits, extrasCatalog, extraIds, selExtras, extrasTotal, exitFee]);

  if (!config || !graves || !trial || !credits) return <Loading />;

  const selectedGrave = graves.find((g) => g.id === graveId);
  if (type === "abbonamento" && !plan) return <Loading label="Caricamento del piano…" />;

  if (graves.length === 0) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-5" data-testid="checkout-no-grave">
        <h1 className="font-serif text-3xl text-navy">Prima registriamo il luogo.</h1>
        <p className="text-body text-sm leading-relaxed">
          Per poter intervenire ci servono il cimitero, la posizione approssimativa e qualche dettaglio.
          Ci vuole un minuto.
        </p>
        <Link to={`/area-cliente/tombe/nuova?next=${encodeURIComponent(`/checkout?type=${type}${planId ? `&plan=${planId}` : ""}`)}`} data-testid="add-grave-cta" className="btn-primary">
          Registra il luogo
        </Link>
      </div>
    );
  }

  const submit = async () => {
    if (type === "extra" && extraIds.length === 0) {
      toast.error("Seleziona almeno un Extra da ordinare.");
      return;
    }
    if (type === "abbonamento" && !flowerId) {
      toast.error("Scegli la composizione floreale prima di procedere.");
      return;
    }
    if (type === "abbonamento" && selExtras.some((e) => e.recurrence === "compleanno") && !birthday) {
      toast.error("Inserisci la data del compleanno da ricordare.");
      return;
    }
    if (!delegation) {
      toast.error("Conferma la delega a intervenire nel luogo indicato.");
      return;
    }
    setBusy(true);
    try {
      const payload = { grave_id: graveId, payment_method: method, extras: extraIds, delegation };
      const { data } =
        type === "singola"
          ? await api.post("/customer/orders/single", { ...payload, flower_id: flowerId, cleaning, notes })
          : type === "extra"
            ? await api.post("/customer/orders/extras", { ...payload, notes })
            : await api.post("/customer/orders/subscription", { ...payload, plan_id: planId, flower_id: flowerId, cleaning, birthday_date: birthday || "" });
      if (data.quote_required || method === "bonifico" || method === "contanti") {
        setDone(data);
        sessionStorage.removeItem(STORAGE_KEY);
        toast.success(data.quote_required ? "Richiesta inviata." : "Ordine confermato.");
      } else {
        // Carta/PayPal: ordine registrato in attesa, il pagamento si completa qui con PayPal.
        setPending(data);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doneGrave = graves.find((g) => g.id === graveId);
  const donePlan = plan;
  const doneFlower = flowers.find((f) => f.id === flowerId);
  const METHOD_LABELS = { carta: "Carta (online)", paypal: "PayPal", bonifico: "Bonifico bancario", contanti: "CONTANTI" };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiato.`);
    } catch {
      toast.error("Copia non riuscita.");
    }
  };

  // Riepilogo ordine per WhatsApp: coerente con il metodo scelto (contanti/bonifico/online).
  const orderMsg = done
    ? [
        "Riepilogo ordine SEMPRE ACCANTO:",
        `Numero ordine: ${(done.payment?.id || "").slice(0, 8).toUpperCase()}`,
        `Nome e cognome: ${done.payment?.customer_name || ""}`,
        done.payment?.customer_phone ? `Telefono: ${done.payment.customer_phone}` : null,
        `Servizio: ${type === "singola" ? `Singola uscita (${doneFlower?.name || "fiori inclusi"})` : type === "extra" ? `Consegna Extra (${selExtras.map((e) => e.name).join(", ")})` : `Abbonamento ${donePlan?.title || ""}`}`,
        type === "abbonamento" && donePlan ? `Quantità: ${donePlan.interventions_total} interventi` : "Quantità: 1",
        `Importo totale: ${eur(done.payment?.amount)}`,
        `Metodo di pagamento: ${METHOD_LABELS[done.payment?.method] || done.payment?.method || ""}`,
        `Stato pagamento: ${done.payment?.status === "completato" ? "PAGATO" : done.payment?.method === "contanti" ? "DA RISCUOTERE" : "IN ATTESA DI BONIFICO"}`,
        doneGrave ? `Persona ricordata: ${doneGrave.deceased_name} ${doneGrave.deceased_surname}` : null,
        doneGrave?.cemetery?.name ? `Luogo: ${doneGrave.cemetery.name}${doneGrave.cemetery.comune ? ` — ${doneGrave.cemetery.comune}` : ""}` : null,
        notes ? `Note: ${notes}` : null,
        done.payment?.method === "bonifico" ? `Beneficiario: ${config?.bank_holder || ""}` : null,
        done.payment?.method === "bonifico" ? `IBAN: ${config?.bank_iban || ""}` : null,
        done.payment?.method === "bonifico" ? `Causale: ${done.payment?.causale || ""}` : null,
        done.payment?.method === "contanti" ? "Vorrei concordare il pagamento in contanti e l'appuntamento. Grazie." : null,
      ].filter(Boolean).join("\n")
    : "";

  const declareBonifico = async () => {
    try {
      await api.post(`/customer/payments/${done.payment.id}/declare-bonifico`);
      setDone({ ...done, payment: { ...done.payment, status: "in_verifica" } });
      toast.success("Grazie! Verificheremo l'accredito e attiveremo il servizio.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (pending && !done) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 space-y-5" data-testid="checkout-paypal">
        <p className="eyebrow mb-2">Pagamento sicuro</p>
        <h1 className="font-serif text-3xl text-navy">Completa il pagamento</h1>
        <p className="text-body text-sm leading-relaxed">
          Il tuo ordine è registrato. Completa il pagamento di <span className="font-semibold text-navy">{eur(pending.payment?.amount)}</span> per attivarlo.
        </p>
        <PayPalCheckout
          paymentId={pending.payment.id}
          amount={pending.payment.amount}
          method={method}
          onSuccess={() => {
            setDone(pending);
            setPending(null);
            sessionStorage.removeItem(STORAGE_KEY);
            toast.success("Pagamento confermato.");
          }}
          onCancel={() => setPending(null)}
        />
        <button data-testid="paypal-back" onClick={() => setPending(null)} className="btn-secondary w-full">
          Torna al riepilogo
        </button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-5" data-testid="checkout-success">
        <CheckCircle2 className="mx-auto text-[#4a7c59]" size={52} />
        <h1 className="font-serif text-3xl text-navy">
          {done.quote_required ? "Richiesta inviata." : "Grazie, è tutto confermato."}
        </h1>
        <p className="text-body text-sm leading-relaxed">
          {done.quote_required
            ? "Valuteremo lo stato del luogo e ti invieremo un preventivo personalizzato per la pulizia profonda."
            : "Abbiamo preso in carico la tua richiesta. Al termine dell'intervento troverai la fotografia nella tua area riservata."}
        </p>
        {done.payment?.method === "bonifico" && (
          <div className="card-soft p-6 text-left space-y-3" data-testid="bonifico-details">
            <p className="eyebrow">Pagamento con bonifico bancario</p>
            <p className="text-sm text-body leading-relaxed">
              Il tuo ordine è registrato e resta in attesa finché non riceviamo il pagamento.
              Effettua il bonifico con questi dati:
            </p>
            <div className="text-sm space-y-2">
              <div className="flex justify-between gap-4"><span className="text-muted-soft">Intestatario</span><span className="font-semibold text-navy">{config?.bank_holder || "—"}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-soft">IBAN</span><span className="font-semibold text-navy">{config?.bank_iban || "—"}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-soft">Importo da pagare</span><span className="font-semibold text-navy">{eur(done.payment?.amount)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-soft">Causale</span><span className="font-semibold text-navy text-right">{done.payment?.causale || `SEMPRE ACCANTO - ORDINE #${(done.payment?.id || "").slice(0, 8).toUpperCase()} - ${done.payment?.customer_name || ""}`}</span></div>
            </div>
            <div className="flex gap-2 pt-1 flex-wrap">
              <button type="button" data-testid="copia-iban" onClick={() => copyText(config?.bank_iban || "", "IBAN")} className="btn-secondary !py-2 !px-4 text-xs">Copia IBAN</button>
              <button type="button" data-testid="copia-causale" onClick={() => copyText(done.payment?.causale || "", "Causale")} className="btn-secondary !py-2 !px-4 text-xs">Copia causale</button>
            </div>
            <p className="text-sm text-body">Ordine registrato. Effettua il bonifico utilizzando i dati indicati sopra.</p>
            {!config?.bank_iban && (
              <p className="text-xs text-muted-soft">Le coordinate bancarie complete ti verranno comunicate anche via email. Per urgenze scrivici su WhatsApp.</p>
            )}
            {done.payment?.status === "in_verifica" ? (
              <p className="text-sm text-[#1e3a5f] bg-[#f0f7ff] border border-[#dbe7f3] rounded-xl px-4 py-3" data-testid="bonifico-in-verifica">
                Hai dichiarato di aver effettuato il bonifico: l'ordine resta in attesa di verifica e sarà confermato appena l'accredito sarà controllato.
              </p>
            ) : (
              <>
                <p className="text-xs text-muted-soft">Dopo aver effettuato il bonifico, segnalacelo: l'ordine resterà in attesa di verifica fino al controllo dell'accredito da parte nostra.</p>
                <button data-testid="bonifico-declared" onClick={declareBonifico} className="btn-primary w-full">
                  Ho effettuato il bonifico
                </button>
              </>
            )}
            <WhatsAppLink message={orderMsg} label="Invia riepilogo su WhatsApp" testid="riepilogo-whatsapp-bonifico" className="btn-secondary !py-2.5 text-sm" />
          </div>
        )}
        {done.payment?.method === "contanti" && (
          <div className="card-soft p-6 text-left space-y-3" data-testid="contanti-details">
            <p className="eyebrow">Pagamento in contanti</p>
            <p className="text-sm text-body leading-relaxed">
              Il tuo ordine è registrato e resta in attesa. Per il pagamento in contanti è necessario
              concordare personalmente pagamento e appuntamento con noi.
            </p>
            <WhatsAppLink message={orderMsg} label="Contattaci su WhatsApp" testid="contanti-whatsapp" className="btn-primary w-full justify-center !text-white" />
            <p className="text-xs text-muted-soft">Il messaggio si apre già compilato con il riepilogo del tuo ordine: ti basta inviarlo.</p>
          </div>
        )}
        {(done.payment?.method === "carta" || done.payment?.method === "paypal") && !done.quote_required && (
          <p className="text-sm text-[#2f5d3e] bg-[#e8f0eb] rounded-xl px-4 py-3" data-testid="payment-confirmed-note">
            Pagamento confermato da PayPal. Il servizio è attivo.
          </p>
        )}
        {!done.quote_required && (
          <WhatsAppLink message={orderMsg} label="Invia riepilogo su WhatsApp" testid="riepilogo-whatsapp" className="btn-secondary mx-auto" />
        )}
        {done.is_trial && (
          <p className="text-sm text-[#2f5d3e] bg-[#e8f0eb] rounded-xl px-4 py-3">
            Hai usato la prova: il tuo bonus prima uscita è attivo per 10 giorni, utilizzabile una sola volta con un abbonamento di almeno 5 interventi.
          </p>
        )}
        {done.credit_applied > 0 && (
          <p className="text-sm text-[#2f5d3e] bg-[#e8f0eb] rounded-xl px-4 py-3">
            Bonus prima uscita applicato: −{eur(done.credit_applied)}.
          </p>
        )}
        <Link to="/area-cliente" data-testid="goto-dashboard" className="btn-primary">Vai alla tua area</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="checkout-page">
      <div className="mb-6"><BackNav /></div>
      <p className="eyebrow mb-2">Conferma richiesta</p>
      <h1 className="font-serif text-4xl text-navy mb-8">
        {type === "singola" ? "Singola uscita" : type === "extra" ? "Ordine Extra" : "Attiva abbonamento"}
      </h1>

      <div className="grid gap-8 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <div className="card-soft p-6 space-y-4">
            <Field label="Luogo" testid="field-grave">
              <select data-testid="select-grave" className="input-soft" value={graveId} onChange={(e) => setGraveId(e.target.value)}>
                {graves.map((g) => (
                  <option key={g.id} value={g.id}>
                    {`${g.deceased_name} ${g.deceased_surname} — ${g.cemetery?.name || ""}`}
                  </option>
                ))}
              </select>
              <Link to="/area-cliente/tombe/nuova" data-testid="add-another-grave" className="text-xs text-[#4a7c59] font-semibold mt-2 inline-block">
                + Registra un altro luogo
              </Link>
            </Field>
          </div>

          <div className="card-soft p-6 space-y-5">
            {type !== "extra" && (
            <Field label={type === "singola" ? "Fiori" : "Composizione floreale (obbligatoria)"} testid="field-flowers">
                <div className={`grid gap-3 ${type === "singola" ? "grid-cols-2 sm:grid-cols-4" : "sm:grid-cols-3"}`} data-testid="flower-picker">
                  {type === "singola" && (
                    <button
                      type="button"
                      data-testid="flower-standard"
                      onClick={() => setFlowerId("")}
                      className={`rounded-xl border p-3 text-left text-sm transition-colors duration-200 ${!flowerId ? "border-[#4a7c59] bg-[#e8f0eb]" : "border-[#e2e8f0] bg-white"}`}
                    >
                      <p className="font-semibold text-navy">Standard</p>
                      <p className="text-xs text-muted-soft">Mazzo di margherite, incluso</p>
                    </button>
                  )}
                  {flowers.filter((f) => type === "abbonamento" || f.name !== "Mazzo di Margherite").map((f) => (
                    <button
                      type="button"
                      key={f.id}
                      data-testid={`flower-option-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setFlowerId(f.id)}
                      className={`rounded-xl border p-3 text-left text-sm transition-colors duration-200 ${flowerId === f.id ? "border-[#4a7c59] bg-[#e8f0eb]" : "border-[#e2e8f0] bg-white"}`}
                    >
                      <p className="font-semibold text-navy">{f.name}</p>
                      <p className="text-xs text-muted-soft">{eur(f.price)}</p>
                    </button>
                  ))}
                </div>
                {type === "abbonamento" && !flowerId && (
                  <p className="text-xs font-semibold text-[#9a3412]" data-testid="flower-required-hint">
                    Seleziona una composizione floreale per continuare.
                  </p>
                )}
                <p className="text-xs text-muted-soft leading-relaxed" data-testid="flower-seasonality-note">
                  Il colore e le varietà possono variare in base alla disponibilità e alla stagionalità.
                  Puoi indicare una preferenza nelle note: faremo il possibile per rispettarla.
                </p>
              </Field>
            )}
            {type !== "extra" && (
              <Field label="Pulizia" testid="field-cleaning">
                <div className={`grid gap-3 ${type === "singola" ? "sm:grid-cols-3" : "sm:grid-cols-2"}`} data-testid="cleaning-picker">
                  {(type === "singola"
                    ? [
                        ["inclusa", "Cura ordinaria", "Inclusa nell'intervento"],
                        ["approfondita", "Pulizia approfondita", `+${eur(config.cleaning_ordinary_price)}`],
                        ["specialistica", "Pulizia specialistica", "Su preventivo"],
                      ]
                    : [
                        ["inclusa", "Cura ordinaria", "Inclusa in ogni intervento"],
                        ["approfondita", "Pulizia approfondita", `+${eur(config.cleaning_ordinary_price)} a intervento`],
                      ]
                  ).map(([id, label, hint]) => (
                    <button
                      type="button"
                      key={id}
                      data-testid={`cleaning-${id}`}
                      onClick={() => setCleaning(id)}
                      className={`rounded-xl border p-3 text-left text-sm transition-colors duration-200 ${cleaning === id ? "border-[#4a7c59] bg-[#e8f0eb]" : "border-[#e2e8f0] bg-white"}`}
                    >
                      <p className="font-semibold text-navy">{label}</p>
                      {hint && <p className="text-xs text-muted-soft">{hint}</p>}
                    </button>
                  ))}
                </div>
              </Field>
            )}
              {(type === "singola" || type === "extra") && (
              <Field label="Note per il professionista (facoltative)" testid="field-notes">
                <textarea data-testid="input-notes" className="input-soft" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Es. anniversario il 18, preferenza per fiori bianchi…" />
              </Field>
              )}
          </div>

          {extrasCatalog.length > 0 && (
            <div className="card-soft p-6 space-y-3" data-testid="extras-picker-card">
              <p className="label-soft">{type === "extra" ? "Extra scelti" : type === "singola" ? "Extra (facoltativi)" : "Ricorrenze (facoltative)"}</p>
              {type === "extra" ? (
                <p className="text-xs text-muted-soft leading-relaxed" data-testid="extra-delivery-hint">
                  {deliveryInfo?.free_delivery
                    ? "Hai già un intervento programmato: consegneremo i tuoi Extra gratuitamente durante la stessa visita."
                    : `Non risulta un intervento già programmato: si applica il supplemento di consegna di ${eur(config.extra_exit_fee)}, una sola volta, per l'uscita dedicata.`}
                </p>
              ) : type === "singola" ? (
                <p className="text-xs text-muted-soft leading-relaxed">
                  Gli Extra scelti saranno consegnati durante questa stessa uscita: la consegna è inclusa,
                  senza nessun supplemento.
                </p>
              ) : (
                <p className="text-xs text-muted-soft leading-relaxed">
                  I prodotti delle Ricorrenze vengono consegnati durante un intervento già programmato,
                  nella settimana della ricorrenza: paghi solo il prodotto, senza costi di uscita aggiuntivi.
                </p>
              )}
              <div className="grid sm:grid-cols-2 gap-3" data-testid="extras-picker">
                {extrasCatalog.map((e) => (
                  <button
                    type="button"
                    key={e.id}
                    data-testid={`extra-option-${e.id}`}
                    onClick={() => setExtraIds((ids) => (ids.includes(e.id) ? ids.filter((x) => x !== e.id) : [...ids, e.id]))}
                    className={`rounded-xl border p-3 text-left text-sm transition-colors duration-200 ${extraIds.includes(e.id) ? "border-[#4a7c59] bg-[#e8f0eb]" : "border-[#e2e8f0] bg-white"}`}
                  >
                    <span className="flex gap-3 items-start">
                      {type !== "singola" && e.image && (
                        <img
                          src={e.image}
                          alt={e.name}
                          data-testid={`extra-img-${e.id}`}
                          onClick={(ev) => { ev.stopPropagation(); setZoomImg({ src: e.image, alt: e.name }); }}
                          className="h-20 w-16 rounded-lg object-cover shrink-0 cursor-zoom-in border border-[#ece7dd]"
                        />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="flex items-start justify-between gap-2">
                          <span>
                            {type !== "singola" && e.recurrence && (
                              <p className="text-[11px] font-bold uppercase tracking-wide text-[#4a7c59]">{REC_LABELS[e.recurrence]}</p>
                            )}
                            <p className="font-semibold text-navy">{e.name}</p>
                          </span>
                          <span aria-hidden="true" className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${extraIds.includes(e.id) ? "border-[#4a7c59] bg-[#4a7c59] text-white" : "border-[#cbd5e1] bg-white"}`}>
                            {extraIds.includes(e.id) && <Check size={13} strokeWidth={3} />}
                          </span>
                        </span>
                        <p className="text-xs text-muted-soft mt-1">+{eur(e.price)}{type === "abbonamento" || (type === "extra" && deliveryInfo?.free_delivery) ? " · Consegna inclusa" : ""}</p>
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              {type === "abbonamento" && selExtras.some((e) => e.recurrence === "compleanno") && (
                <div className="mt-1" data-testid="birthday-block">
                  <label className="label-soft" htmlFor="birthday-date-input">Data del compleanno da ricordare</label>
                  <input
                    id="birthday-date-input"
                    data-testid="birthday-date-input"
                    type="date"
                    className="input-soft w-full"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                  />
                  <p className="text-xs text-muted-soft mt-1">Consegneremo la composizione in quella data, durante un intervento già programmato.</p>
                </div>
              )}
            </div>
          )}

          <div className="card-soft p-6 space-y-3">
            <p className="label-soft">Metodo di pagamento</p>
            <div className="grid sm:grid-cols-3 gap-3" data-testid="payment-methods">
              {METHODS.map((m) => (
                <button
                  type="button"
                  key={m.id}
                  data-testid={`pay-${m.id}`}
                  onClick={() => setMethod(m.id)}
                  className={`rounded-xl border p-4 text-left transition-colors duration-200 ${method === m.id ? "border-[#1e3a5f] bg-[#f0f7ff]" : "border-[#e2e8f0] bg-white"}`}
                >
                  <m.icon size={20} className="text-navy mb-2" />
                  <p className="font-semibold text-navy text-sm">{m.label}</p>
                  <p className="text-[11px] text-muted-soft leading-snug">{m.hint}</p>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-soft">Pagamenti online sicuri tramite PayPal: conto PayPal o carta, anche senza registrazione.</p>
          </div>
        </div>

        <div className="card-ivory p-6 h-fit space-y-4 sticky top-24" data-testid="checkout-summary">
          <p className="eyebrow">Riepilogo</p>
          {selectedGrave?.cemetery && (
            <div className="rounded-xl bg-white/70 border border-[#ece7dd] px-3 py-2.5" data-testid="summary-cemetery">
              <p className="text-xs font-semibold text-navy">{selectedGrave.deceased_name} {selectedGrave.deceased_surname}</p>
              <p className="text-xs text-navy">{selectedGrave.cemetery.name}</p>
              <p className="text-[11px] text-muted-soft">{selectedGrave.cemetery.address}</p>
            </div>
          )}
          {selectedGrave?.map_x != null && selectedGrave?.cemetery?.aerial_image && (
            <div className="space-y-1.5" data-testid="summary-map-preview">
              <div className="relative w-full overflow-hidden rounded-xl border border-[#ece7dd] bg-[#eef2f7] flex justify-center">
                <span className="relative inline-block">
                  <img src={selectedGrave.cemetery.aerial_image} alt="Mappa del cimitero" className="block max-h-44 max-w-full w-auto object-contain" />
                  <span className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ left: `${selectedGrave.map_x * 100}%`, top: `${selectedGrave.map_y * 100}%` }}>
                    <span className="block h-4 w-4 rounded-full bg-[#4a7c59] border-[3px] border-white shadow-md" />
                  </span>
                </span>
              </div>
              <p className="text-xs text-[#2f5d3e] font-medium">Posizione del luogo confermata ✓</p>
            </div>
          )}
          {summary?.rows.map(([label, value]) => (
            <div key={label} className="flex justify-between text-sm text-navy gap-3">
              <span>{label}</span>
              <span className="font-semibold whitespace-nowrap">{value}</span>
            </div>
          ))}
          {type === "abbonamento" && plan && (
            <p className="text-xs text-muted-soft">
              {plan.interventions_total} interventi ({plan.frequency} al mese per {plan.duration_months} mesi) a partire da oggi, {fmtDate(new Date().toISOString())}.
            </p>
          )}
          {type === "abbonamento" && plan && credits?.bonus?.status === "disponibile" && plan.interventions_total < 5 && (
            <p className="text-xs text-muted-soft" data-testid="bonus-min-hint">
              Il bonus prima uscita si applica agli abbonamenti con almeno 5 interventi totali.
            </p>
          )}
          <div className="border-t border-[#ece7dd] pt-3 flex justify-between items-baseline">
            <span className="text-sm font-semibold text-navy">{summary?.quote ? "Totale" : "Totale da pagare"}</span>
            <span data-testid="summary-total" className="font-serif text-3xl text-navy">
              {summary?.quote ? "Su preventivo" : eur(summary?.total ?? 0)}
            </span>
          </div>
          {type === "singola" && summary?.isTrial && !summary?.quote && (
            <p className="text-xs text-[#2f5d3e] bg-[#e8f0eb] rounded-xl px-3 py-2">
              Prima uscita di prova: hai un bonus valido 10 giorni, utilizzabile una sola volta con un abbonamento di almeno 5 interventi.
            </p>
          )}
          <label data-testid="delegation-label" className="flex items-start gap-3 rounded-xl border border-[#e2e8f0] bg-white p-3 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="delegation-checkbox"
              checked={delegation}
              onChange={(e) => setDelegation(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 accent-[#4a7c59]"
            />
            <span className="text-xs text-body leading-relaxed">
              Confermo di delegare SEMPRE ACCANTO a intervenire presso il luogo da me indicato, per mio conto e secondo quanto ordinato.
            </span>
          </label>
          <button
            data-testid="checkout-submit"
            onClick={submit}
            disabled={busy || !graveId || !delegation || (type === "abbonamento" && !flowerId) || (type === "extra" && extraIds.length === 0)}
            className="btn-primary w-full disabled:opacity-60"
          >
            {busy ? "Un momento…" : summary?.quote ? "Invia richiesta di preventivo" : "Conferma e paga"}
          </button>
        </div>
      </div>
      {zoomImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setZoomImg(null)}
          data-testid="extra-img-zoom"
        >
          <img src={zoomImg.src} alt={zoomImg.alt} className="max-h-[85vh] max-w-full rounded-2xl object-contain bg-white" />
        </div>
      )}
    </div>
  );
}
